import { Formik } from "formik";
import { z } from "zod";
import { withZodSchema } from "formik-validator-zod";
import { useRsvp } from "../api";
import { useToken } from "./TokenProvider";
import { Text, TextWithFallback } from "./i18n";
import { shortText } from "../../model/rsvp";
import React, { useEffect } from "react";
import { Input } from "./Input";
import { useNavigate } from "react-router";
import { Logo } from "./Logo";

const accessTokenSchema = z.object({
  accessToken: shortText,
});

export const Login: React.FC = () => {
  const tokenContext = useToken();
  const rsvp = useRsvp(tokenContext.token);
  const navigate = useNavigate();

  useEffect(() => {
    if (rsvp.data && !rsvp.error) {
      navigate("/rsvp", { replace: true });
    }
  }, [rsvp.data, rsvp.error, navigate]);

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <Logo />
          <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
            <Text id="login.title" />
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <Formik
            initialValues={{ accessToken: "" }}
            validate={withZodSchema(accessTokenSchema)}
            onSubmit={(values, { setSubmitting }) => {
              tokenContext.setToken(values.accessToken);
              setSubmitting(false);
            }}
          >
            {({ values, errors, handleChange, handleBlur, handleSubmit }) => (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    name="accessToken"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.accessToken}
                    className="mt-2"
                    label={<Text id="login.accessToken" />}
                    error={
                      errors.accessToken ? (
                        <TextWithFallback
                          id={errors.accessToken}
                          fallbackId="errors.validation.generic"
                        />
                      ) : null
                    }
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    <Text id="login.signin" />
                  </button>
                  <p className="mt-2">
                    {rsvp.error && (
                      <TextWithFallback
                        id={rsvp.error.message}
                        fallbackId="errors.1000"
                      />
                    )}
                  </p>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};
